import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { initCatalog } from '@/utils/customProducts';
import { initAvailability } from '@/utils/availability';
import { initReview } from '@/utils/review';
import { initPhotos } from '@/utils/photoStatus';
import { initSiteConfig } from '@/utils/siteConfig';
import { Toaster } from 'sonner';
import { SolicitationProvider } from '@/context/SolicitationContext';
import { QueueProvider } from '@/context/QueueContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileRequestBar from '@/components/MobileRequestBar';
import Home from '@/pages/Home';
import CategoryPage from '@/pages/CategoryPage';
import ProductPage from '@/pages/ProductPage';
import SearchPage from '@/pages/SearchPage';
import RequestPage from '@/pages/RequestPage';
import CheckoutPage from '@/pages/CheckoutPage';
import SuccessPage from '@/pages/SuccessPage';
import NossaLoja from '@/pages/NossaLoja';
import Privacidade from '@/pages/Privacidade';
import CategoriesPage from '@/pages/CategoriesPage';
import { AdminLogin, AdminLayout, AdminOnly } from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminDetail from '@/pages/admin/AdminDetail';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminNewProduct from '@/pages/admin/AdminNewProduct';
import AdminAvailability from '@/pages/admin/AdminAvailability';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminReview from '@/pages/admin/AdminReview';
import AdminPersonalization from '@/pages/admin/AdminPersonalization';
import AdminCategories from '@/pages/admin/AdminCategories';
import AdminBatchProduct from '@/pages/admin/AdminBatchProduct';
import AdminPhotos from '@/pages/admin/AdminPhotos';
import AdminEditProduct from '@/pages/admin/AdminEditProduct';
import AdminAIIntegrations from '@/pages/admin/AdminAIIntegrations';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  return (
    <div className="flex min-h-screen flex-col">
      {!isAdmin && <Header />}
      <div className={`flex-1 ${isAdmin ? '' : 'pb-20 md:pb-0'}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/categorias" element={<CategoriesPage />} />
          <Route path="/categoria/:slug" element={<CategoryPage />} />
          <Route path="/produto/:slug" element={<ProductPage />} />
          <Route path="/busca" element={<SearchPage />} />
          <Route path="/solicitacao" element={<RequestPage />} />
          <Route path="/finalizar" element={<CheckoutPage />} />
          <Route path="/sucesso" element={<SuccessPage />} />
          <Route path="/nossa-loja" element={<NossaLoja />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/solicitacao/:id" element={<AdminDetail />} />
            <Route path="/admin/disponibilidade" element={<AdminAvailability />} />
            <Route path="/admin/revisao" element={<AdminReview />} />
            <Route path="/admin/personalizacao" element={<AdminPersonalization />} />
            <Route path="/admin/categorias" element={<AdminCategories />} />
            <Route path="/admin/produtos/lote" element={<AdminOnly><AdminBatchProduct /></AdminOnly>} />
            <Route path="/admin/fotos" element={<AdminPhotos />} />
            <Route path="/admin/produtos/editar/:slug" element={<AdminOnly><AdminEditProduct /></AdminOnly>} />
            <Route path="/admin/integracoes-ia" element={<AdminAIIntegrations />} />
            <Route path="/admin/usuarios" element={<AdminUsers />} />
            <Route path="/admin/produtos" element={<AdminOnly><AdminProducts /></AdminOnly>} />
            <Route path="/admin/produtos/novo" element={<AdminOnly><AdminNewProduct /></AdminOnly>} />
          </Route>
        </Routes>
      </div>
      {!isAdmin && <Footer />}
      {!isAdmin && <MobileRequestBar />}
    </div>
  );
}

function CatalogLoading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAFAF7]" data-testid="catalog-loading">
      <span className="font-serif text-2xl font-semibold tracking-wide text-navy">
        H<span className="font-normal">UDDERSFIELD</span>
      </span>
      <span className="text-[10px] font-semibold tracking-[0.35em] text-navy/80">TECIDOS</span>
      <p className="mt-4 text-sm text-muted-foreground">Carregando catálogo…</p>
    </div>
  );
}

function App() {
  // Dados reais (MongoDB via API) carregados antes do primeiro render: catálogo,
  // disponibilidade, revisão e Personalização do Site — em qualquer navegador,
  // após logout e novos deploys.
  const [dataReady, setDataReady] = useState(false);
  useEffect(() => {
    Promise.allSettled([initCatalog(), initAvailability(), initReview(), initPhotos(), initSiteConfig()]).then(() => setDataReady(true));
  }, []);
  if (!dataReady) return <CatalogLoading />;
  return (
    <BrowserRouter>
      <QueueProvider>
        <SolicitationProvider>
        <ScrollToTop />
        <AppShell />
        <Toaster position="top-center" richColors />
        </SolicitationProvider>
      </QueueProvider>
    </BrowserRouter>
  );
}

export default App;
